import React, { useEffect, useState } from "react";
import {
  Container,
  Heading,
  Checkbox,
  Input,
  Select,
  Button,
  SimpleGrid,
  useToast,
  useColorModeValue,
  HStack,
  Skeleton,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import Navbar from "../../components/Navbar";
import useAxios from "../../utils/useAxios";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import IrrigationControllerTile from "../../components/IrrigationComponents/IrrigationControllerTile";

const IrrigationOverview: React.FC = () => {
  const [data, setData] = useState<any[] | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [modeFilter, setModeFilter] = useState<string>("all");
  const api = useAxios();
  const toast = useToast();
  const [loading, setLoading] = useState<boolean>(true);
  const [flowDataLoading, setFlowDataLoading] = useState<Set<number>>(new Set());
  const [pendingCommandLoading, setPendingCommandLoading] = useState<Set<number>>(new Set());
  const hasFetchedRef = React.useRef(false);

  const fetchFlowData = async (controllerId: number) => {
    try {
      const response = await api.get(`/agtech/irrigation/${controllerId}/flow-measurement/`);
      // Update the specific controller with flow data
      setData(
        (prevData) =>
          prevData?.map((item) =>
            item.id === controllerId ? { ...item, flow_measurement: response.data } : item,
          ) || null,
      );
    } catch (error: any) {
      // Silently fail if no flow data available (e.g., not a flow sensor)
      console.log(`No flow data for controller ${controllerId}`);
    } finally {
      setFlowDataLoading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(controllerId);
        return newSet;
      });
    }
  };

  const fetchPendingCommand = async (controllerId: number) => {
    try {
      const response = await api.get(`/agtech/irrigation/${controllerId}/pending-command/`);
      // Update the specific controller with pending command data
      setData(
        (prevData) =>
          prevData?.map((item) =>
            item.id === controllerId ? { ...item, pending_valve_command: response.data } : item,
          ) || null,
      );
    } catch (error: any) {
      // Silently fail if endpoint has issues
      console.log(`Error fetching pending command for controller ${controllerId}`);
    } finally {
      setPendingCommandLoading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(controllerId);
        return newSet;
      });
    }
  };

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const response = await api.get("/agtech/irrigation/");
      setData(response.data);

      // Fetch flow data and pending commands in parallel for all controllers
      const controllerIds = response.data.map((item: any) => item.id);
      setFlowDataLoading(new Set(controllerIds));
      setPendingCommandLoading(new Set(controllerIds));
      controllerIds.forEach((id: number) => {
        fetchFlowData(id);
        fetchPendingCommand(id);
      });
    } catch (error: any) {
      toast({
        title: <Trans>Error fetching irrigation overview</Trans>,
        description: error.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch once per component mount
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchOverview();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPolling) interval = setInterval(() => fetchOverview(), 5000);
    return () => clearInterval(interval);
  }, [isPolling]);

  const filtered = data?.filter((item) => {
    const matchesName = item.controller_device_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesMode = modeFilter === "all" ? true : item.mode === Number(modeFilter);
    return matchesName && matchesMode;
  });

  return (
    <Navbar>
      <Container maxW="container.2xl" mt={10} p={5}>
        <Heading as="h1" size="2xl" textAlign="center" mb={8} color="dark.500">
          <Trans>Irrigation Overview</Trans>
        </Heading>

        <Checkbox
          isChecked={isPolling}
          onChange={(e) => setIsPolling(e.target.checked)}
          size="lg"
          mb={6}
        >
          <Trans>Enable live updates</Trans>
        </Checkbox>

        <HStack mb={6} spacing={4}>
          <Input
            placeholder={t`Search by device name...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select w="200px" value={modeFilter} onChange={(e) => setModeFilter(e.target.value)}>
            <option value="all">All Modes</option>
            <option value="0">Manual</option>
            <option value="1">Schedule</option>
            <option value="2">Auto</option>
          </Select>
          <Button
            onClick={() => fetchOverview()}
            leftIcon={<RepeatIcon />}
            variant="outline"
            colorScheme="teal"
            size="md"
            flexShrink={0}
          >
            <Trans>Refresh</Trans>
          </Button>
        </HStack>

        <Skeleton
          isLoaded={!loading}
          fadeDuration={0.3}
          startColor={useColorModeValue("gray.200", "gray.700")}
        >
          <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} spacing={6}>
            {filtered?.map((irrigation) => (
              <IrrigationControllerTile
                key={irrigation.id}
                irrigation={irrigation}
                flowDataLoading={flowDataLoading.has(irrigation.id)}
              />
            ))}
          </SimpleGrid>
        </Skeleton>
      </Container>
    </Navbar>
  );
};

export default IrrigationOverview;
